import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swords, Flame, Activity, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar, Spinner } from "@/components/Bits";
import { shareOrCopy, signed } from "@/lib/share";
import { eventText } from "@/pages/league/ActivityTab";
import { RivalriesCard, RecapCard } from "@/pages/league/SocialCards";
import BestNightCard from "@/components/BestNightCard";
import MemberInvite from "@/components/MemberInvite";
import RecruitersCard from "@/components/RecruitersCard";
import AdSlot from "@/components/AdSlot";

export const timeLeft = (end) => {
  const ms = new Date(end) - new Date();
  if (ms <= 0) return "terminé";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  return d > 0 ? `${d} j ${h} h` : `${h} h`;
};

export default function OverviewTab({ league }) {
  const [st, setSt] = useState(null);
  const [chs, setChs] = useState(null);
  const [feed, setFeed] = useState(null);
  useEffect(() => {
    api.get(`/leagues/${league.id}/standings`).then(({ data }) => setSt(data.rows));
    api.get(`/leagues/${league.id}/challenges`).then(({ data }) => setChs(data));
    api.get(`/leagues/${league.id}/activity`, { params: { limit: 5 } }).then(({ data }) => setFeed(data));
  }, [league.id]);
  if (!st || !chs || !feed) return <Spinner />;
  const ranked = st.filter((r) => r.rank);
  const main = chs.find((c) => c.status === "active") || chs.find((c) => ["accepted", "pending"].includes(c.status));
  const shareRanking = () => shareOrCopy({ title: league.name, text: `Classement « ${league.name} » : ${ranked.slice(0, 3).map((r) => `${r.rank}. ${r.display_name} (${r.points})`).join(", ")}` });
  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="league-overview">
      <MemberInvite league={league} />
      <RecruitersCard league={league} />
      <AdSlot placement="league_home" leagueId={league.id} className="lg:col-span-2" />
      <section className="mpl-card p-5">
        <p className="mpl-eyebrow">Période en cours</p>
        <p className="mt-1 font-display text-2xl font-extrabold uppercase" data-testid="overview-period">{league.current_period.label}</p>
        <p className="mt-1 text-xs text-slate-400">{league.current_period.type_label} · barème {league.scoring_rule.label}</p>
        {ranked.length >= 2 ? (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <div className="rounded-xl bg-[#B7F52A]/10 p-3"><p className="text-[10px] font-bold uppercase text-[#B7F52A]">En tête</p><p className="mt-1 flex items-center gap-2 font-bold"><Avatar user={ranked[0]} size={24} /> {ranked[0].display_name}</p><p className="font-mono-num text-sm text-[#B7F52A]">{signed(ranked[0].points)} pts</p></div>
            <div className="rounded-xl bg-red-400/10 p-3"><p className="text-[10px] font-bold uppercase text-red-300">Dernière place</p><p className="mt-1 flex items-center gap-2 font-bold"><Avatar user={ranked[ranked.length - 1]} size={24} /> {ranked[ranked.length - 1].display_name}</p><p className="font-mono-num text-sm text-red-300">{signed(ranked[ranked.length - 1].points)} pts</p></div>
          </div>
        ) : <p className="mt-4 text-sm text-slate-400">Le classement apparaîtra après les premiers résultats officiels.</p>}
        <div className="mt-4 flex gap-2">
          <Link to={`/ligues/${league.id}/classement`} className="mpl-btn-ghost" data-testid="overview-ranking-link">Classement complet</Link>
          {ranked.length > 0 && <button onClick={shareRanking} className="mpl-btn-ghost" data-testid="overview-share-ranking"><Share2 size={14} /> Partager</button>}
        </div>
      </section>
      <section className="mpl-card p-5" data-testid="overview-challenge">
        <p className="mpl-eyebrow flex items-center gap-1.5"><Swords size={13} /> Défi principal</p>
        {main ? (
          <>
            <p className="mt-1 font-display text-2xl font-extrabold uppercase leading-tight">{main.title}</p>
            <p className="mt-1 text-sm text-slate-300">{main.consequence}</p>
            <p className="mt-3 text-xs text-slate-400">{main.period_label} · {main.status_label} · {main.participant_ids.length} participants</p>
            {main.status === "active" && <p className="mt-1 font-mono-num text-sm font-bold text-[#B7F52A]">Fin dans {timeLeft(main.end)}</p>}
          </>
        ) : <p className="mt-2 text-sm text-slate-400">{league.observer ? "Aucun défi en cours." : "Aucun défi en cours. Lancez-en un pour pimenter la compétition !"}</p>}
        <Link to={`/ligues/${league.id}/defis`} className="mpl-btn-primary mt-4" data-testid="overview-challenges-link">{main || league.observer ? "Voir les défis" : "Lancer un défi"}</Link>
      </section>
      <BestNightCard leagueId={league.id} />
      <RivalriesCard leagueId={league.id} />
      <RecapCard leagueId={league.id} />
      <AdSlot placement="weekly_recap" leagueId={league.id} compact className="lg:col-span-2" />
      <AdSlot placement="promo_card" leagueId={league.id} className="lg:col-span-2" />
      <section className="mpl-card p-5 lg:col-span-2">
        <p className="mpl-eyebrow mb-3 flex items-center gap-1.5"><Activity size={13} /> Dernière activité</p>
        {feed.length ? feed.map((e) => (
          <p key={e.id} className="flex items-start gap-2 border-b border-white/[0.04] py-2 text-sm last:border-0"><Flame size={14} className="mt-0.5 shrink-0 text-[#B7F52A]" /> <span className="min-w-0 [overflow-wrap:anywhere]">{eventText(e)}</span></p>
        )) : <p className="text-sm text-slate-400">Rien pour l'instant.</p>}
        <Link to={`/ligues/${league.id}/activite`} className="mt-3 inline-block text-xs font-semibold text-[#B7F52A]" data-testid="overview-activity-link">Tout le fil d'activité →</Link>
      </section>
    </div>
  );
}
